import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CanalPedido, PedidoResumo } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import BuscaPecas from '../componentes/BuscaPecas';
import CampoBusca from '../componentes/CampoBusca';
import CampoNumero from '../componentes/CampoNumero';
import Ordenacao from '../componentes/Ordenacao';
import {
  avisarClienteDaEncomenda, cancelarSaldoItem, fornecedores, itensDoPedido,
  listarPedidos, recebidoPorItem, receberPedido, salvarPedido, type ItemDoPedido,
} from '../lib/consultas';
import { brl, hojeISO, paraNumero, qtd } from '../lib/formato';
import { casaBusca, porData, porNumero, porTexto } from '../lib/listas';
import { mensagemDeErro } from '../lib/supabase';

// O que a loja PEDIU ao fornecedor (migração 040). Não confundir com
// "Encomendas", que é peça separada para um CLIENTE — o nome das duas coisas
// no balcão é o mesmo, e por isso esta tela se chama Compras.
//
// A situação nunca é digitada: sai dos itens. A tela abre no que está EM
// ABERTO, que é a pergunta do dia ("o que estou esperando?").

const SITUACOES: Record<string, string> = {
  rascunho: 'rascunho', enviado: 'aguardando', recebido_parcial: 'chegou em parte',
  recebido: 'recebido', cancelado: 'cancelado',
};
const dataBR = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

type OrdemCompra = 'recente' | 'antigo' | 'entrega' | 'maior' | 'menor' | 'fornecedor';

const ORDENS_COMPRA = [
  { id: 'recente', rotulo: 'Pedido mais recente' },
  { id: 'antigo', rotulo: 'Pedido mais antigo' },
  { id: 'entrega', rotulo: 'Entrega prevista antes' },
  { id: 'maior', rotulo: 'Maior valor' },
  { id: 'menor', rotulo: 'Menor valor' },
  { id: 'fornecedor', rotulo: 'Fornecedor (A–Z)' },
] as const satisfies ReadonlyArray<{ id: OrdemCompra; rotulo: string }>;

/** Passou da entrega prevista e ainda não fechou — a régua da tarja vermelha,
 *  reaproveitada pelo filtro "só os atrasados" para que os dois concordem. */
const estaAtrasado = (p: PedidoResumo, hoje: string): boolean => Boolean(
  p.previsao_entrega && p.previsao_entrega < hoje
  && p.situacao !== 'recebido' && p.situacao !== 'cancelado',
);

export default function Compras() {
  const cliente = useQueryClient();
  const [abertos, setAbertos] = useState(true);
  const [criando, setCriando] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<OrdemCompra>('recente');
  const [soAtrasados, setSoAtrasados] = useState(false);
  // "O que já chegou e o cliente ainda não sabe" é a pergunta que faz a loja
  // perder venda: a peça está na prateleira e quem encomendou continua esperando.
  const [soAvisar, setSoAvisar] = useState(false);

  const pedidos = useQuery({
    queryKey: ['pedidos', abertos], queryFn: () => listarPedidos(abertos),
  });
  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['pedidos'] });
    cliente.invalidateQueries({ queryKey: ['itens-pedido'] });
    cliente.invalidateQueries({ queryKey: ['buscar'] });
    cliente.invalidateQueries({ queryKey: ['contas-pagar'] });
    // Receber a peça pendente a libera (093): a fila e o sino mudam junto.
    cliente.invalidateQueries({ queryKey: ['fila-conferencia'] });
    cliente.invalidateQueries({ queryKey: ['sino', 'para-liberar'] });
  };

  const hoje = hojeISO();

  const linhas = useMemo(() => {
    const filtrados = (pedidos.data ?? []).filter((p) => (
      casaBusca(busca, String(p.numero), p.fornecedor, p.numero_no_fornecedor,
                p.condicao_pagamento, p.observacoes)
      && (!soAtrasados || estaAtrasado(p, hoje))
      && (!soAvisar || p.encomendas_a_avisar > 0)
    ));
    return [...filtrados].sort((a, b) => {
      if (ordem === 'antigo') return porData(a.data_pedido, b.data_pedido, 'asc');
      if (ordem === 'maior') return porNumero(Number(a.valor_pedido), Number(b.valor_pedido), 'desc');
      if (ordem === 'menor') return porNumero(Number(a.valor_pedido), Number(b.valor_pedido), 'asc');
      if (ordem === 'fornecedor') {
        return porTexto(a.fornecedor, b.fornecedor) || porData(a.data_pedido, b.data_pedido);
      }
      // Pedido SEM previsão cai no fim: não dá para cobrar entrega de quem
      // nunca prometeu data.
      if (ordem === 'entrega') {
        return porData(a.previsao_entrega, b.previsao_entrega, 'asc');
      }
      return porData(a.data_pedido, b.data_pedido);
    });
  }, [pedidos.data, busca, soAtrasados, soAvisar, ordem, hoje]);

  const filtrando = busca.trim() !== '' || soAtrasados || soAvisar;
  const aAvisar = (pedidos.data ?? []).filter((p) => p.encomendas_a_avisar > 0).length;

  return (
    <Layout titulo="Compras">
      <div className="space-y-4">
        <div className="cartao flex flex-wrap items-center justify-between gap-2 p-3">
          <div className="flex gap-2">
            {([[true, 'Em aberto'], [false, 'Todos']] as const).map(([v, t]) => (
              <button key={t} type="button" onClick={() => setAbertos(v)}
                      aria-pressed={abertos === v}
                      className={`border px-3 py-1 text-xs font-bold ${
                        abertos === v ? 'border-signal bg-signal/10' : 'border-edge text-mute'}`}>
                {t}
              </button>
            ))}
          </div>
          <button type="button" className="botao-primario px-3 py-2 text-sm"
                  onClick={() => setCriando(true)}>
            + Pedido
          </button>
        </div>

        <div className="cartao space-y-3 p-3">
          <CampoBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar por nº do pedido, fornecedor ou nota do fornecedor"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Ordenacao opcoes={ORDENS_COMPRA} valor={ordem} aoMudar={setOrdem} />
            <p className="text-xs text-dim">
              {filtrando ? `${linhas.length} de ${pedidos.data?.length ?? 0}` : linhas.length} pedido(s)
            </p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={soAtrasados}
                     onChange={(e) => setSoAtrasados(e.target.checked)} />
              Só os atrasados
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={soAvisar}
                     onChange={(e) => setSoAvisar(e.target.checked)} />
              Só com cliente a avisar
              {aAvisar > 0 && <span className="font-bold text-erro">({aAvisar})</span>}
            </label>
          </div>
        </div>

        {criando && (
          <FormularioPedido aoFechar={() => setCriando(false)}
                            aoSalvar={() => { setCriando(false); recarregar(); }} />
        )}

        {pedidos.isPending ? <Carregando /> : null}
        {pedidos.error ? <Aviso tipo="erro">{mensagemDeErro(pedidos.error)}</Aviso> : null}

        <ul className="space-y-2">
          {linhas.map((p) => {
            const atrasado = estaAtrasado(p, hoje);
            return (
              <li key={p.id} className={`cartao p-3 ${atrasado ? 'border-erro/50' : ''}`}>
                <button type="button" className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
                        onClick={() => setAberto(aberto === p.id ? null : p.id)}>
                  <div className="min-w-0">
                    <p className="text-sm font-bold">
                      Pedido {p.numero} · {p.fornecedor}
                      {p.numero_no_fornecedor ? (
                        <span className="ml-1 font-mono text-xs text-dim">
                          ({p.numero_no_fornecedor})
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-mute">
                      {dataBR(p.data_pedido)} · entrega {dataBR(p.previsao_entrega)}
                      {atrasado ? ' · ATRASADO' : ''}
                      {p.condicao_pagamento ? ` · ${p.condicao_pagamento}` : ''}
                    </p>
                    <p className="mt-1 flex flex-wrap items-center gap-1 text-[0.625rem] uppercase">
                      <span className={`border px-1 ${
                        p.situacao === 'recebido' ? 'border-ok/50 text-ok'
                          : p.situacao === 'recebido_parcial' ? 'border-alerta/50 text-alerta'
                          : p.situacao === 'cancelado' ? 'border-edge text-dim'
                          : 'border-signal/50 text-signal-soft'}`}>
                        {SITUACOES[p.situacao] ?? p.situacao}
                      </span>
                      {p.itens_pendentes > 0 && (
                        <span className="text-dim">{p.itens_pendentes} item(ns) a chegar</span>
                      )}
                      {p.encomendas_a_avisar > 0 && (
                        <span className="border border-erro/50 px-1 text-erro">
                          avisar {p.encomendas_a_avisar} cliente(s)
                        </span>
                      )}
                    </p>
                  </div>
                  <span className="tabular-nums text-lg font-black">{brl(Number(p.valor_pedido))}</span>
                </button>
                {aberto === p.id && (
                  <DetalhePedido pedido={p} aoMudar={recarregar} />
                )}
              </li>
            );
          })}
          {linhas.length === 0 && !pedidos.isPending && (
            <li className="text-sm text-dim">
              {filtrando && (pedidos.data?.length ?? 0) > 0
                ? 'Nenhum pedido casa com esses filtros.'
                : abertos ? 'Nenhum pedido em aberto.' : 'Nenhum pedido registrado.'}
            </li>
          )}
        </ul>
      </div>
    </Layout>
  );
}

/** Os itens, o que já chegou, e as duas ações que fecham o ciclo. */
function DetalhePedido({ pedido, aoMudar }: { pedido: PedidoResumo; aoMudar: () => void }) {
  const [recebendo, setRecebendo] = useState(false);
  const itens = useQuery({
    queryKey: ['itens-pedido', pedido.id], queryFn: () => itensDoPedido(pedido.id),
  });
  const recebido = useQuery({
    queryKey: ['itens-pedido', pedido.id, 'recebido'], queryFn: () => recebidoPorItem(pedido.id),
  });

  const avisar = useMutation({ mutationFn: avisarClienteDaEncomenda, onSuccess: aoMudar });
  const cancelarSaldo = useMutation({ mutationFn: cancelarSaldoItem, onSuccess: aoMudar });

  const chegou = (id: string) => Number(recebido.data?.[id] ?? 0);

  return (
    <div className="mt-3 space-y-3 border-t border-edge pt-3">
      <ul className="space-y-1">
        {(itens.data ?? []).map((i) => {
          const falta = Number(i.quantidade) - chegou(i.id);
          return (
            <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="min-w-0">
                <strong>{qtd(Number(i.quantidade))}×</strong> {i.descricao}
                {i.codigo_fabricante ? <span className="ml-1 font-mono text-dim">{i.codigo_fabricante}</span> : null}
                {i.tipo !== 'compra' ? <span className="ml-1 text-alerta">({i.tipo})</span> : null}
                {i.encomenda_cliente_nome ? (
                  <span className="ml-1 text-signal-soft">
                    · encomenda de {i.encomenda_cliente_nome}
                    {i.encomenda_cliente_telefone ? ` (${i.encomenda_cliente_telefone})` : ''}
                  </span>
                ) : null}
              </span>
              <span className="flex items-center gap-2 text-dim">
                {chegou(i.id) > 0 ? `chegaram ${qtd(chegou(i.id))}` : 'nada ainda'}
                {i.saldo_cancelado && <span className="text-dim">· saldo cancelado</span>}
                {/* O aviso que evita estoque morto: chegou e o cliente não sabe. */}
                {i.encomenda_cliente_nome && !i.cliente_avisado && chegou(i.id) > 0 && (
                  <button type="button" className="border border-erro/50 px-1 text-erro"
                          disabled={avisar.isPending}
                          onClick={() => avisar.mutate(i.id)}>
                    avisei o cliente
                  </button>
                )}
                {falta > 0 && !i.saldo_cancelado && (
                  <button type="button" className="underline"
                          disabled={cancelarSaldo.isPending}
                          onClick={() => cancelarSaldo.mutate(i.id)}>
                    não vem
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {avisar.error ? <Aviso tipo="erro">{mensagemDeErro(avisar.error)}</Aviso> : null}
      {cancelarSaldo.error ? <Aviso tipo="erro">{mensagemDeErro(cancelarSaldo.error)}</Aviso> : null}

      {pedido.situacao !== 'recebido' && pedido.situacao !== 'cancelado' && (
        recebendo ? (
          <PainelRecebimento pedido={pedido} itens={itens.data ?? []} chegou={chegou}
                             aoFechar={() => setRecebendo(false)}
                             aoReceber={() => { setRecebendo(false); aoMudar(); }} />
        ) : (
          <button type="button" className="botao-primario px-3 py-2 text-sm"
                  onClick={() => setRecebendo(true)}>
            Registrar chegada
          </button>
        )
      )}
    </div>
  );
}

/**
 * A chegada. Numa transação só: entra no estoque (abatendo a dívida de
 * registro), o custo da NOTA vira o custo real da peça, e nasce a conta a
 * pagar do que chegou — nunca do que foi pedido.
 */
function PainelRecebimento({ pedido, itens, chegou, aoFechar, aoReceber }: {
  pedido: PedidoResumo;
  itens: Array<{ id: string; descricao: string; quantidade: number; custo_unitario: number; saldo_cancelado: boolean }>;
  chegou: (id: string) => number;
  aoFechar: () => void;
  aoReceber: () => void;
}) {
  const [qtds, setQtds] = useState<Record<string, string>>({});
  const [custos, setCustos] = useState<Record<string, string>>({});
  const [nf, setNf] = useState({ numero: '', serie: '', chave: '', valor: '' });
  const [parcelas, setParcelas] = useState('1');
  const [vencimento, setVencimento] = useState('');

  const escolhidos = itens
    .map((i) => ({
      item_id: i.id,
      quantidade: paraNumero(qtds[i.id] ?? '') ?? 0,
      custo_unitario_recebido: paraNumero(custos[i.id] ?? '') ?? null,
    }))
    .filter((e) => e.quantidade > 0);

  const receber = useMutation({
    mutationFn: () => receberPedido({
      pedidoId: pedido.id,
      itens: escolhidos,
      nfNumero: nf.numero || null,
      nfSerie: nf.serie || null,
      nfChave: nf.chave || null,
      nfValor: paraNumero(nf.valor),
      parcelas: Number(parcelas) || 0,
      primeiroVencimento: vencimento || null,
    }),
    onSuccess: aoReceber,
  });

  return (
    <div className="space-y-3 border border-signal/40 bg-signal/5 p-3">
      <p className="text-xs text-mute">
        O que chegou entra no estoque agora. O <strong>custo da nota</strong> vira o
        custo real da peça, e a conta a pagar nasce do que <strong>chegou</strong> —
        não do que foi pedido.
      </p>
      <ul className="space-y-2">
        {itens.filter((i) => !i.saldo_cancelado && chegou(i.id) < Number(i.quantidade)).map((i) => (
          <li key={i.id} className="grid grid-cols-[1fr_5rem_6rem] items-center gap-2 text-xs">
            <span className="min-w-0 truncate">
              {i.descricao}
              <span className="ml-1 text-dim">
                (falta {qtd(Number(i.quantidade) - chegou(i.id))})
              </span>
            </span>
            <input className="campo py-1 text-right" placeholder="qtd" inputMode="decimal"
                   aria-label={`Quantidade recebida de ${i.descricao}`}
                   value={qtds[i.id] ?? ''}
                   onChange={(e) => setQtds((a) => ({ ...a, [i.id]: e.target.value }))} />
            <input className="campo py-1 text-right" inputMode="decimal"
                   aria-label={`Custo da nota de ${i.descricao}`}
                   placeholder={`R$ ${Number(i.custo_unitario).toFixed(2)}`}
                   value={custos[i.id] ?? ''}
                   onChange={(e) => setCustos((a) => ({ ...a, [i.id]: e.target.value }))} />
          </li>
        ))}
      </ul>

      <div className="grid gap-2 sm:grid-cols-4">
        <input className="campo py-1 text-sm" placeholder="NF nº" value={nf.numero}
               aria-label="Número da nota fiscal"
               onChange={(e) => setNf((a) => ({ ...a, numero: e.target.value }))} />
        <input className="campo py-1 text-sm" placeholder="Série" value={nf.serie}
               aria-label="Série da nota" onChange={(e) => setNf((a) => ({ ...a, serie: e.target.value }))} />
        <input className="campo py-1 text-sm" placeholder="Valor da NF" value={nf.valor}
               inputMode="decimal" aria-label="Valor total da nota"
               onChange={(e) => setNf((a) => ({ ...a, valor: e.target.value }))} />
        <input className="campo py-1 font-mono text-xs" placeholder="Chave (44 dígitos)"
               value={nf.chave} aria-label="Chave de acesso da NF-e"
               onChange={(e) => setNf((a) => ({ ...a, chave: e.target.value }))} />
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="rc-parc">Parcelas da conta a pagar</label>
          <input id="rc-parc" className="campo py-1 text-sm" value={parcelas} inputMode="numeric"
                 onChange={(e) => setParcelas(e.target.value)} />
          <p className="mt-1 text-xs text-dim">
            0 = não gerar conta (garantia e bonificação já ficam de fora sozinhas).
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor="rc-venc">1º vencimento</label>
          <input id="rc-venc" type="date" className="campo py-1 text-sm" value={vencimento}
                 onChange={(e) => setVencimento(e.target.value)} />
          <p className="mt-1 text-xs text-dim">As demais caem de 30 em 30 dias.</p>
        </div>
      </div>

      {receber.error ? <Aviso tipo="erro">{mensagemDeErro(receber.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={receber.isPending || escolhidos.length === 0}
                onClick={() => receber.mutate()}>
          {receber.isPending ? 'Registrando…' : 'Registrar chegada'}
        </button>
      </div>
    </div>
  );
}

/** Montar o pedido. Peça do catálogo entra pela busca; o resto, digitado. */
function FormularioPedido({ aoFechar, aoSalvar }: { aoFechar: () => void; aoSalvar: () => void }) {
  const forn = useQuery({ queryKey: ['fornecedores'], queryFn: fornecedores });
  const [f, setF] = useState({
    fornecedorId: '', dataPedido: hojeISO(), previsao: '', canal: 'whatsapp' as CanalPedido,
    numeroNoFornecedor: '', condicao: '', frete: '', desconto: '', observacoes: '',
  });
  const [itens, setItens] = useState<ItemDoPedido[]>([]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((a) => ({ ...a, [k]: v }));

  const alterarItem = (i: number, campo: keyof ItemDoPedido, valor: string) =>
    setItens((a) => a.map((it, j) => (j === i ? { ...it, [campo]: valor } : it)));
  const alterarItemNumero = (i: number, campo: 'quantidade' | 'custo_unitario', valor: number) =>
    setItens((a) => a.map((it, j) => (j === i ? { ...it, [campo]: valor } : it)));

  const salvar = useMutation({
    mutationFn: () => salvarPedido({
      fornecedorId: f.fornecedorId, dataPedido: f.dataPedido,
      previsao: f.previsao || null, canal: f.canal,
      numeroNoFornecedor: f.numeroNoFornecedor || null, condicao: f.condicao || null,
      valorFrete: paraNumero(f.frete) ?? 0, desconto: paraNumero(f.desconto) ?? 0,
      observacoes: f.observacoes || null, itens,
    }),
    onSuccess: aoSalvar,
  });

  const total = itens.reduce((s, i) => s + i.quantidade * i.custo_unitario, 0)
    + (paraNumero(f.frete) ?? 0) - (paraNumero(f.desconto) ?? 0);

  return (
    <div className="cartao space-y-3 p-4">
      <h2 className="rotulo">Novo pedido ao fornecedor</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="rotulo" htmlFor="pd-forn">Fornecedor</label>
          <select id="pd-forn" className="campo" value={f.fornecedorId}
                  onChange={(e) => set('fornecedorId', e.target.value)}>
            <option value="">escolha…</option>
            {(forn.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="rotulo" htmlFor="pd-data">Data do pedido</label>
          <input id="pd-data" type="date" className="campo" value={f.dataPedido}
                 onChange={(e) => set('dataPedido', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="pd-prev">Previsão de entrega</label>
          <input id="pd-prev" type="date" className="campo" value={f.previsao}
                 onChange={(e) => set('previsao', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="pd-canal">Pedido por</label>
          <select id="pd-canal" className="campo" value={f.canal}
                  onChange={(e) => set('canal', e.target.value as CanalPedido)}>
            <option value="whatsapp">WhatsApp</option>
            <option value="telefone">Telefone</option>
            <option value="portal">Portal</option>
            <option value="representante">Representante</option>
            <option value="balcao">Balcão</option>
          </select>
        </div>
        <div>
          <label className="rotulo" htmlFor="pd-num">Nº no fornecedor</label>
          <input id="pd-num" className="campo" value={f.numeroNoFornecedor}
                 placeholder="o número que ELES deram"
                 onChange={(e) => set('numeroNoFornecedor', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="pd-cond">Condição de pagamento</label>
          <input id="pd-cond" className="campo" value={f.condicao} placeholder="28/35/42"
                 onChange={(e) => set('condicao', e.target.value)} />
        </div>
      </div>

      <div className="border-t border-edge pt-3">
        <p className="rotulo mb-2">Peças pedidas</p>
        <BuscaPecas
          onEscolher={(p) => setItens((a) => [...a, {
            product_id: p.product_id, descricao: p.descricao,
            codigo_fabricante: p.codigo_fabrica, quantidade: 1, custo_unitario: 0,
            tipo: 'compra',
          }])}
        />
        <button type="button" className="botao-secundario mt-2 px-3 py-1 text-xs"
                onClick={() => setItens((a) => [...a, {
                  descricao: '', quantidade: 1, custo_unitario: 0, tipo: 'compra',
                }])}>
          + Item que não está no catálogo
        </button>

        <ul className="mt-3 space-y-2">
          {itens.map((i, idx) => (
            <li key={idx} className="grid grid-cols-[1fr_4rem_5rem_2rem] items-center gap-2 text-xs">
              <input className="campo py-1" value={i.descricao} placeholder="descrição da peça"
                     aria-label="Descrição do item"
                     onChange={(e) => alterarItem(idx, 'descricao', e.target.value)} />
              {/* CampoNumero pelo mesmo motivo da tela de Venda: digitar
                   vírgula num campo controlado por número zerava o valor. */}
              <CampoNumero className="campo py-1 text-right" valor={i.quantidade}
                           aria-label="Quantidade"
                           onValor={(n) => alterarItemNumero(idx, 'quantidade', n ?? 1)} />
              <CampoNumero className="campo py-1 text-right" valor={i.custo_unitario}
                           aria-label="Custo unitário"
                           onValor={(n) => alterarItemNumero(idx, 'custo_unitario', n ?? 0)} />
              <button type="button" className="text-erro"
                      aria-label="Remover item"
                      onClick={() => setItens((a) => a.filter((_, j) => j !== idx))}>×</button>
              <input className="campo col-span-4 py-1" placeholder="Encomenda para o cliente (nome) — opcional"
                     aria-label="Cliente da encomenda"
                     value={i.encomenda_cliente_nome ?? ''}
                     onChange={(e) => alterarItem(idx, 'encomenda_cliente_nome', e.target.value)} />
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="rotulo" htmlFor="pd-frete">Frete (R$)</label>
          <input id="pd-frete" className="campo" value={f.frete} inputMode="decimal"
                 onChange={(e) => set('frete', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="pd-desc">Desconto (R$)</label>
          <input id="pd-desc" className="campo" value={f.desconto} inputMode="decimal"
                 onChange={(e) => set('desconto', e.target.value)} />
        </div>
        <div className="flex items-end justify-end">
          <span className="text-lg font-black">{brl(total)}</span>
        </div>
      </div>

      <input className="campo" value={f.observacoes} placeholder="Observações"
             aria-label="Observações do pedido"
             onChange={(e) => set('observacoes', e.target.value)} />

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={salvar.isPending || !f.fornecedorId || itens.length === 0
                          || itens.some((i) => !i.descricao.trim())}
                onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar pedido'}
        </button>
      </div>
    </div>
  );
}
